import { DEFAULT_PARAMS, objectsRHandler } from '@/utils/objectsRHandler';

import { getApiData, API_TYPES } from '@/utils/modelsApi';
import { ROLES } from '@/constants/roles';

import EventAssociation from '../list/event-association/index';
import AssociationOffice from '../list/event-association/office';

import Event from '../list/event';
import { apiService } from '@/services/APIService';
import { rHandler } from '@/plugins/RHandler';
import AssociationClub from '../list/event-association/club';
import AssociationEventRequest from '../list/event-association/event-request';
import AssociationUser from '../list/event-association/user';
import AssociationAttribute from '../list/event-association/attribute';
import AssociationAccreditationOrganizer from '../list/event-association/accreditation-organizer';
import { corvertQuery } from '@/modules/UserEventLanding/functions';
import { PER_PAGE } from '@/constants/pagination';
import { EventManager } from './event';

const PATH = 'event-associations';

const PRIVATE_ROLES = [ROLES.organization, ROLES.admin, ROLES.certificate, ROLES.member, ROLES.trainer];
const API_DATA = {
  [API_TYPES.private]: {
    path: PATH,
    get objects() {
      const { path } = this;
      return {
        ...objectsRHandler(path, EventAssociation),
      };
    },
  },
  [API_TYPES.public]: {
    path: `public/${PATH}`,
    get objects() {
      const { path } = this;
      return {
        ...objectsRHandler(path, EventAssociation),
      };
    },
  },
};

export const EventAssociationsManager = {
  async forEvent({ args, params = {} } = DEFAULT_PARAMS) {
    const [eventId] = args;
    const result = await EventManager.objects.show({ args: [eventId], params: { expand: 'associations' } });
    if (result.error) return result;
    const associations = result.item.associations || [];
    if (!associations.length) return { ...result, items: [] };
    return this.objects.index({
      params: {
        'search[id]': associations.map(({ id }) => id),
        'per-page': PER_PAGE.infinity,
        ...params,
      },
    });
  },
  get objects() {
    return {
      ...getApiData(API_DATA, PRIVATE_ROLES).objects,
    };
  },
  get features() {
    const pathRequest = 'event-associations-features';
    return {
      index({ params } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.get(`/${pathRequest}`, params),
          (response) => ({
            response,
            items: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get requests() {
    const pathRequest = 'requests';
    return {
      index({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: (response.data.data || []).map((item) => new AssociationEventRequest(item)),
          }),
          (error) => ({ error }),
        );
      },
      list({ params } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.get(`/${PATH}/${pathRequest}`, params),
          (response) => ({
            response,
            items: (response.data.data || []).map((item) => new AssociationEventRequest(item)),
          }),
          (error) => ({ error }),
        );
      },
      show({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${pathRequest}/${id}`, params),
          (response) => ({
            response,
            item: new AssociationEventRequest(response.data.data || {}),
          }),
          (error) => ({ error }),
        );
      },
      request({ args, data } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.post(`/${PATH}/${pathRequest}`, data),
          (response) => ({
            response,
            item: new AssociationEventRequest(response.data.data || {}),
          }),
          (error) => ({ error }),
        );
      },
      accept({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${pathRequest}/${id}/accept`, data),
          (response) => ({
            response,
            item: new AssociationEventRequest(response.data.data || {}),
          }),
          (error) => ({ error }),
        );
      },
      reject({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${pathRequest}/${id}/reject`, data),
          (response) => ({
            response,
            item: new AssociationEventRequest(response.data.data || {}),
          }),
          (error) => ({ error }),
        );
      },
      reset({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${pathRequest}/${id}/reset`, data),
          (response) => ({
            response,
            item: new AssociationEventRequest(response.data.data || {}),
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get events() {
    const pathRequest = 'events';
    return {
      index({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: (response.data.data || []).map((i) => new Event(i, 'events')),
          }),
          (error) => ({ error }),
        );
      },
      show({ args, params } = DEFAULT_PARAMS) {
        const [id, eventId] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}/${eventId}`, params),
          (response) => ({
            response,
            item: new Event(response.data.data || {}, 'events'),
          }),
          (error) => ({ error }),
        );
      },
      attach({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/attach`, data),
          (response) => ({
            response,
            item: new Event(response.data.data || {}, 'events'),
          }),
          (error) => ({ error }),
        );
      },
      detach({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/detach`, data),
          (response) => ({
            response,
            item: new Event(response.data.data || {}, 'events'),
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get organizers() {
    const pathRequest = 'organizers';
    return {
      index({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      attach({ args, data } = DEFAULT_PARAMS) {
        const [id, orgId] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/${orgId}/attach`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      detach({ args, data } = DEFAULT_PARAMS) {
        const [id, orgId] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/${orgId}/detach`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get clubs() {
    const pathRequest = 'clubs';
    return {
      index({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: (response.data.data || []).map((i) => new AssociationClub(i)),
          }),
          (error) => ({ error }),
        );
      },
      request({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/request`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      remove({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/remove`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      confirm({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/confirm`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      reject({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/reject`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      reset({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/reset`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get users() {
    const pathRequest = 'users';
    return {
      index({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: (response.data.data || []).map((i) => new AssociationUser(i)),
          }),
          (error) => ({ error }),
        );
      },
      show({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      request({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      change({ args, data } = DEFAULT_PARAMS) {
        const [id, userId] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/${userId}/change`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      accept({ args, data } = DEFAULT_PARAMS) {
        const [id, userId] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/${userId}/accept`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      reject({ args, data } = DEFAULT_PARAMS) {
        const [id, userId] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/${userId}/reject`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      reset({ args, data } = DEFAULT_PARAMS) {
        const [id, userId] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/${userId}/reset`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get attributes() {
    const path = 'association-global-attributes';
    return {
      ...objectsRHandler(path, AssociationAttribute),
      attach({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/attach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      detach({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/detach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
    };
  },
  get associationAttributes() {
    const pathRequest = 'attributes';
    return {
      index({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: (response.data.data || []).map((item) => new AssociationAttribute(item)),
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get accreditationOrganizers() {
    const pathRequest = 'accreditation-organizers';
    return {
      index({ args, params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${PATH}/${id}/${pathRequest}`, params),
          (response) => ({
            response,
            items: (response.data.data || []).map((i) => new AssociationAccreditationOrganizer(i)),
          }),
          (error) => ({ error }),
        );
      },
      request({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      remove({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.delete(`/${PATH}/${id}/${pathRequest}`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      accept({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/accept`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      reject({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/reject`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      reset({ args, data } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.post(`/${PATH}/${id}/${pathRequest}/reset`, data),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get offices() {
    const path = 'representative-office-invites';
    return {
      ...objectsRHandler(`/representative-offices`, AssociationOffice),
      showInvite({ data, params } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.post(`/${path}/show-info`, data, { params }),
          (response) => ({
            response,
            item: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
      acceptInvite({ data } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.post(`/${path}/accept`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
    };
  },
};
